// ═══════════════════════════════════════════════════════════════
// OpenGov EMC — Hub Dashboard
// Three-column layout: Prepare → Respond → Recover
// ═══════════════════════════════════════════════════════════════

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconFlame, IconArrowsExchange, IconClipboardCheck, IconMap2,
  IconBuildingCommunity, IconUserPlus, IconCalendarEvent, IconAntenna,
  IconFolderOpen, IconChalkboard, IconShieldCheck, IconHeartHandshake,
  IconBackhoe, IconHomePlus, IconHomeExclamation, IconFileInvoice, IconGitMerge, IconForms,
  IconClock, IconAlertTriangle,
} from '@tabler/icons-react';
import { IconInfoCircle } from '@tabler/icons-react';
import { useRoleStore } from '../../store/roleStore';
import { useAuthStore } from '../../store/authStore';
import { MODULES } from '../../lib/modules';
import type { ModuleId } from '../../types/rbac';
import './Dashboard.css';
import { semantic } from '../../lib/colors';

const GUIDE_MAP: Record<string, string> = {
  hotwash: 'hotwash-101.html', handoff: 'handoff-101.html', rollcall: 'rollcall-101.html',
  geoops: 'geoops-101.html', impact: 'impact-101.html', intake: 'intake-101.html',
  battlerhythm: 'battlerhythm-101.html', pace: 'pace-101.html', volunteer: 'volunteeros-101.html',
  filelibrary: 'filelibrary-101.html', blackboard: 'blackboard-101.html', incidents: 'incidents-101.html',
  mainstreet: 'mainstreet-101.html', debris: 'debris-101.html', shelter: 'shelter-101.html',
  pagrants: 'pagrants-101.html', workflows: 'workflows-101.html',
};

const ICON_MAP: Record<string, React.ComponentType<{ size?: number; stroke?: number }>> = {
  IconFlame, IconArrowsExchange, IconClipboardCheck, IconMap2,
  IconBuildingCommunity, IconUserPlus, IconCalendarEvent, IconAntenna,
  IconFolderOpen, IconChalkboard, IconShieldCheck, IconHeartHandshake,
  IconBackhoe, IconHomePlus, IconHomeExclamation, IconFileInvoice, IconGitMerge, IconForms,
  IconClock,
};

const PHASE_GROUPS: { label: string; color: string; moduleIds: string[] }[] = [
  {
    label: 'Prepare',
    color: '#06B6D4',
    moduleIds: ['pace', 'volunteer', 'filelibrary', 'blackboard', 'mainstreet', 'forms'],
  },
  {
    label: 'Respond',
    color: semantic.error,
    moduleIds: ['handoff', 'rollcall', 'geoops', 'intake', 'battlerhythm', 'shelter'],
  },
  {
    label: 'Recover',
    color: '#07963F',
    moduleIds: ['impact', 'hotwash', 'debris', 'pagrants', 'timelabor', 'workflows'],
  },
];

function hexToRgba(hex: string, alpha: number) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { myRole, getModuleAccess } = useRoleStore();
  const { activeIncident } = useAuthStore();

  useEffect(() => {
    if (myRole?.roleSlug === 'biz_owner') {
      navigate('/mainstreet', { replace: true });
    }
  }, [myRole, navigate]);

  const moduleMap = new Map(MODULES.filter(m => m.id !== 'incidents').map(m => [m.id, m]));

  return (
    <div className="dashboard">
      {/* Incident Status Bar */}
      {activeIncident ? (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
          marginBottom: 16, borderRadius: 8, background: 'var(--mantine-color-red-0)',
          border: '1px solid var(--mantine-color-red-2)',
        }}>
          <IconAlertTriangle size={20} color="var(--mantine-color-red-6)" />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--mantine-color-red-8)' }}>
              Active Incident: {activeIncident.name}
            </div>
            <div style={{ fontSize: 12, color: 'var(--mantine-color-red-6)' }}>
              {(activeIncident as any).type ? `${(activeIncident as any).type} — ` : ''}Activated {(activeIncident as any).startDate || (activeIncident as any).activatedDate || ''}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={() => navigate('/cop')}
              style={{
                padding: '4px 12px', borderRadius: 6, border: '1px solid var(--mantine-color-blue-3)',
                background: 'var(--mantine-color-blue-0)', cursor: 'pointer', fontSize: 12, fontWeight: 500,
                color: 'var(--mantine-color-blue-7)',
              }}
            >
              Live COP
            </button>
            <button
              onClick={() => navigate('/incidents')}
              style={{
                padding: '4px 12px', borderRadius: 6, border: '1px solid var(--mantine-color-red-3)',
                background: 'white', cursor: 'pointer', fontSize: 12, fontWeight: 500,
                color: 'var(--mantine-color-red-7)',
              }}
            >
              Manage
            </button>
          </div>
        </div>
      ) : (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
          marginBottom: 16, borderRadius: 8, background: 'var(--mantine-color-gray-0)',
          border: '1px solid var(--mantine-color-gray-3)',
        }}>
          <IconAlertTriangle size={20} color="var(--mantine-color-gray-5)" />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--mantine-color-gray-7)' }}>
              No Active Incident
            </div>
            <div style={{ fontSize: 12, color: 'var(--mantine-color-gray-5)' }}>
              Select or create an incident from the Incident Hub to activate modules.
            </div>
          </div>
          <button
            onClick={() => navigate('/incidents')}
            style={{
              padding: '4px 12px', borderRadius: 6, border: '1px solid var(--mantine-color-gray-4)',
              background: 'white', cursor: 'pointer', fontSize: 12, fontWeight: 500,
              color: 'var(--mantine-color-gray-7)',
            }}
          >
            Incident Hub
          </button>
        </div>
      )}

      <div className="phase-columns">
        {PHASE_GROUPS.map(phase => {
          const phaseModules = phase.moduleIds
            .map(id => moduleMap.get(id))
            .filter(Boolean)
            .filter(mod => getModuleAccess(mod!.id as ModuleId).canView)
            .map(mod => mod!);

          if (phaseModules.length === 0) return null;

          const isOdd = phaseModules.length % 2 !== 0;

          return (
            <div key={phase.label} className="phase-column">
              <div className="phase-header" style={{ borderColor: phase.color }}>
                <span className="phase-dot" style={{ backgroundColor: phase.color }} />
                <span className="phase-title">{phase.label}</span>
              </div>

              <div className="phase-grid">
                {phaseModules.map((mod, i) => {
                  const Icon = ICON_MAP[mod.icon];
                  const isLast = i === phaseModules.length - 1;
                  const doubleWidth = isOdd && isLast;

                  const guideFile = GUIDE_MAP[mod.id];

                  return (
                    <div
                      key={mod.id}
                      className={`hub-card ${doubleWidth ? 'hub-card-wide' : ''}`}
                      onClick={() => navigate(mod.route)}
                      style={{ '--phase-color': phase.color, '--icon-bg': hexToRgba(mod.color, 0.12), position: 'relative' } as React.CSSProperties}
                    >
                      {guideFile && (
                        <a
                          href={`/guides/${guideFile}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hub-card-info"
                          title={`${mod.name} 101 Guide`}
                          onClick={e => e.stopPropagation()}
                        >
                          <IconInfoCircle size={16} stroke={1.5} />
                        </a>
                      )}
                      <div className="hub-card-icon">
                        {Icon && <Icon size={22} stroke={1.5} />}
                      </div>
                      <div className="hub-card-text">
                        <div className="hub-card-name">{mod.name}</div>
                        <div className="hub-card-desc">{mod.description}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
